import React, { useState, useEffect } from 'react';
import { Book, Chapter } from '../types/Book';
import { ChapterProgress, PathInfo, ChapterNavigationData } from '../types/ChapterNavigation';
import { chapterProgressService } from '../services/chapterProgressService';
import './ChapterNavigation.css';

interface ChapterNavigationProps {
  book: Book;
  currentChapter: Chapter;
  onChapterSelect: (chapterFile: string) => void;
  onClose: () => void;
  isOpen: boolean;
  chapterHistory: any[];
}

const ChapterNavigation: React.FC<ChapterNavigationProps> = ({
  book,
  currentChapter,
  onChapterSelect,
  onClose,
  isOpen,
  chapterHistory
}) => {
  const [activeTab, setActiveTab] = useState<'chapters' | 'paths'>('chapters');
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [navigationData, setNavigationData] = useState<ChapterNavigationData | null>(null);

  // Determine if this is a linear or branching story
  const storyType = book.paths && book.paths.length > 1 ? 'branching' : 'linear';

  useEffect(() => {
    if (book && currentChapter) {
      const data = buildNavigationData();
      setNavigationData(data);
    }
  }, [book, currentChapter, chapterHistory]);

  const buildNavigationData = (): ChapterNavigationData => {
    // Build chapter progress data using the service
    const allChapters = chapterProgressService.buildChapterProgressArray(
      book,
      currentChapter.file,
      chapterHistory
    );

    // Build path information for branching stories
    const availablePaths: PathInfo[] = storyType === 'branching' && book.paths ? 
      book.paths.map(path => ({
        id: path.id,
        name: path.name,
        color: path.color,
        description: path.description,
        chaptersInPath: getChaptersInPath(path.id),
        isCompleted: isPathCompleted(path.id, allChapters)
      })) : [];

    // Get recent chapters using the service
    const recentChapters = chapterProgressService.getRecentChapters(book.id, 5);

    return {
      currentChapter: currentChapter.file,
      allChapters,
      availablePaths,
      recentChapters,
      bookmarkChapters: chapterProgressService.getBookmarks(book.id),
      storyType
    };
  };

  const getChapterPath = (chapter: Chapter): string => {
    if (storyType === 'linear') return 'main';
    return chapter.path_name || 'main';
  };

  const getChaptersInPath = (pathId: string): string[] => {
    return book.chapters
      .filter(chapter => getChapterPath(chapter) === pathId)
      .map(chapter => chapter.file);
  };

  const isPathCompleted = (pathId: string, chapters: ChapterProgress[]): boolean => {
    const pathChapters = getChaptersInPath(pathId);
    return pathChapters.every(chapterFile => 
      chapters.find(c => c.chapterFile === chapterFile)?.isCompleted
    );
  };

  const handleChapterClick = (chapterFile: string) => {
    onChapterSelect(chapterFile);
    // Don't close navigation when chapter is selected - let user manually toggle
  };

  const filteredChapters = navigationData?.allChapters.filter(chapter => {
    const chapterData = book.chapters.find(c => c.file === chapter.chapterFile);
    if (!chapterData) return false;

    // Only show published chapters
    if (chapterData.status !== 'published') {
      return false;
    }

    // Filter by selected path
    if (selectedPath && chapter.pathTaken !== selectedPath) {
      return false;
    }

    return true;
  }) || [];

  const formatAudioPosition = (seconds?: number): string => {
    if (!seconds) return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (!isOpen || !navigationData) return null;

  return (
    <div className="chapter-navigation-overlay" onClick={onClose}>
      <div className="chapter-navigation-modal" onClick={e => e.stopPropagation()}>
        <div className="chapter-navigation-header">
          <h2>Navigate Story</h2>
          <button className="close-button" onClick={onClose} aria-label="Close navigation">
            ✕
          </button>
        </div>

        {/* Filters */}
        {storyType === 'branching' && navigationData.availablePaths.length > 0 && (
          <div className="chapter-navigation-controls">
            <div className="path-filter">
              <label>Filter by path:</label>
              <select 
                value={selectedPath || ''} 
                onChange={(e) => setSelectedPath(e.target.value || null)}
                className="path-select"
              >
                <option value="">All paths</option>
                {navigationData.availablePaths.map(path => (
                  <option key={path.id} value={path.id}>
                    {path.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Tabs for different views */}
        <div className="navigation-tabs">
          <button 
            className={`tab ${activeTab === 'chapters' ? 'active' : ''}`}
            onClick={() => setActiveTab('chapters')}
          >
            Chapters
          </button>
          {storyType === 'branching' && (
            <button 
              className={`tab ${activeTab === 'paths' ? 'active' : ''}`}
              onClick={() => setActiveTab('paths')}
            >
              Story Paths
            </button>
          )}
        </div>

        {/* Main content area */}
        <div className="navigation-content">
          {activeTab === 'chapters' && (
            <div className="chapters-view">

              {/* All chapters list */}
              <div className="all-chapters">
                <h3>All Chapters</h3>
                <div className="chapter-list">
                  {filteredChapters.map((progress, index) => {
                    const chapter = book.chapters.find(c => c.file === progress.chapterFile);
                    if (!chapter) return null;

                    return (
                      <div 
                        key={progress.chapterFile}
                        className={`chapter-item ${progress.isCurrentlyPlaying ? 'current' : ''}`}
                        onClick={() => handleChapterClick(progress.chapterFile)}
                      >
                        <div className="chapter-number">{index + 1}</div>
                        <div className="chapter-status">
                          {progress.isCurrentlyPlaying ? '🎵' : 
                           progress.isCompleted ? '✓' : '📖'}
                        </div>
                        <div className="chapter-info">
                          <span className="chapter-title">{chapter.title}</span>
                          {progress.pathTaken && storyType === 'branching' && (
                            <span className="chapter-path">{progress.pathTaken}</span>
                          )}
                          {progress.audioPosition && (
                            <span className="audio-position">
                              Resume at {formatAudioPosition(progress.audioPosition)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'paths' && storyType === 'branching' && (
            <div className="paths-view">
              <h3>Story Paths</h3>
              <div className="paths-list">
                {navigationData.availablePaths.map(path => (
                  <div key={path.id} className="path-item">
                    <div 
                      className="path-header"
                      style={{ borderLeftColor: path.color }}
                    >
                      <h4>{path.name}</h4>
                      {path.description && <p>{path.description}</p>}
                      <div className="path-progress">
                        {path.isCompleted ? '✓ Completed' : `${path.chaptersInPath.length} chapters`}
                      </div>
                    </div>
                    <div className="path-chapters">
                      {path.chaptersInPath.map(chapterFile => {
                        const chapter = book.chapters.find(c => c.file === chapterFile);
                        const progress = navigationData.allChapters.find(c => c.chapterFile === chapterFile);
                        if (!chapter || !progress) return null;

                        return (
                          <div 
                            key={chapterFile}
                            className={`path-chapter ${progress.isCurrentlyPlaying ? 'current' : ''}`}
                            onClick={() => handleChapterClick(chapterFile)}
                          >
                            <span className="chapter-status">
                              {progress.isCurrentlyPlaying ? '🎵' : 
                               progress.isCompleted ? '✓' : '📖'}
                            </span>
                            <span className="chapter-title">{chapter.title}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ChapterNavigation;